
import { useEffect, useState, useRef } from "react";
import { Merchant, Location, TradeCategory } from "@/types";
import { getAllMerchants, getTradeCategories, calculateDistance } from "@/services/merchantService";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Search, MapPin, Navigation } from "lucide-react";
import MerchantCard from "@/components/merchant/MerchantCard";
import QRCode from "./QRCode";

export default function MerchantListing() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<TradeCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [merchantsWithDistance, setMerchantsWithDistance] = useState<(Merchant & { distance?: number })[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);

  // Fetch user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        error => {
          console.error("Error getting location:", error);
          toast.error("Could not get your location");
        }
      );
    } else {
      toast.error("Geolocation is not supported by this browser");
    }
  }, []);

  // Fetch merchants and categories
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setIsLoading(true);
        const merchantData = await getAllMerchants();
        setMerchants(merchantData);
        
        // Fetch trade categories
        const categoryData = await getTradeCategories();
        const formattedCategories = categoryData.map(category => ({
          value: category,
          label: category
        }));
        
        setCategories([{ value: "", label: "All Categories" }, ...formattedCategories]);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching merchants:", error);
        toast.error("Failed to load merchants");
        setIsLoading(false);
      }
    };

    fetchMerchants();
  }, []);

  // Calculate distances when merchants or user location changes
  useEffect(() => {
    if (userLocation && merchants.length > 0) {
      const withDistance = merchants.map(merchant => {
        let distance = undefined;
        
        if (merchant.latitude && merchant.longitude) {
          distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            merchant.latitude,
            merchant.longitude
          );
        }
        
        return {
          ...merchant,
          distance
        };
      });
      
      // Sort by distance if available
      const sorted = withDistance.sort((a, b) => {
        if (a.distance && b.distance) {
          return a.distance - b.distance;
        }
        return 0;
      });
      
      setMerchantsWithDistance(sorted);
      applyFilters(sorted, searchQuery, selectedCategory);
    } else {
      setMerchantsWithDistance(merchants);
      applyFilters(merchants, searchQuery, selectedCategory);
    }
  }, [merchants, userLocation]);

  const applyFilters = (merchantList: Merchant[], query: string, category: string) => {
    let filtered = merchantList;
    
    // Apply category filter
    if (category) {
      filtered = filtered.filter(merchant => 
        merchant.trade_category === category
      );
    }
    
    // Apply search filter
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(merchant => 
        merchant.business_name.toLowerCase().includes(searchLower) ||
        (merchant.description?.toLowerCase().includes(searchLower)) ||
        (merchant.address?.toLowerCase().includes(searchLower))
      );
    }
    
    setFilteredMerchants(filtered);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(merchantsWithDistance, query, selectedCategory);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    applyFilters(merchantsWithDistance, searchQuery, value);
  };

  const navigateToMerchant = (merchant: Merchant) => {
    if (merchant.latitude && merchant.longitude) {
      // Open in Google Maps
      const url = `https://www.google.com/maps/dir/?api=1&destination=${merchant.latitude},${merchant.longitude}`;
      window.open(url, '_blank');
    } else {
      toast.error("No location data available for this merchant");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 pt-24">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-3xl font-bold mb-4 md:mb-0">Merchants</h1>
        <div className="flex flex-col md:flex-row w-full md:w-auto gap-4">
          <div className="relative w-full md:w-60">
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div className="relative w-full md:w-60">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search merchants..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10 w-full"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : filteredMerchants.length === 0 ? (
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-4">No merchants found</h2>
          <p className="text-gray-600 mb-6">
            {searchQuery || selectedCategory ? "No merchants match your search criteria." : "There are no merchants available at this time."}
          </p>
          {(searchQuery || selectedCategory) && (
            <Button onClick={() => {
              setSearchQuery("");
              setSelectedCategory("");
              setFilteredMerchants(merchantsWithDistance);
            }}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMerchants.map((merchant) => (
            <Card key={merchant.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
              <div className="p-4">
                <div className="mb-4">
                  <MerchantCard merchant={merchant} />
                </div>
                
                {merchant.trade_category && (
                  <div className="mb-4">
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      {merchant.trade_category}
                    </span>
                  </div>
                )}
                
                {merchant.distance !== undefined && (
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{merchant.distance.toFixed(2)} km away</span>
                  </div>
                )}
                
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2">Scan to visit:</h4>
                  <QRCode 
                    value={`${window.location.origin}/merchant/${merchant.id}`} 
                    size={100}
                    backgroundColor="#FFFFFF"
                    foregroundColor="#000000"
                  />
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button asChild>
                    <Link to={`/merchant/${merchant.id}`}>
                      View Details
                    </Link>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => navigateToMerchant(merchant)}
                    disabled={!merchant.latitude || !merchant.longitude}
                  >
                    <Navigation className="h-4 w-4 mr-1" />
                    Navigate
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
